import React, { useState, useEffect } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useContractServices } from '../../context/ContractServicesContext';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { 
  Lock, 
  Clock, 
  Wallet, 
  CheckCircle, 
  AlertCircle, 
  Calendar, 
  ArrowRight, 
  AlertTriangle,
  RefreshCw,
  Shield,
  Hourglass,
  Coins,
  Timer,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import skyLoader from "../../assets/home-assets/skyLoader.gif";

const StakingPortfolioTab = () => {
  const { walletAddress, isConnected } = useWallet();
  const { services, isReady } = useContractServices();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [stakingParams, setStakingParams] = useState(null);
  const [projectStakes, setProjectStakes] = useState([]);
  const [unstaking, setUnstaking] = useState({ projectId: null, stakeIndex: null });
  const [earlyUnstaking, setEarlyUnstaking] = useState({ projectId: null, stakeIndex: null });
  const [penaltyDetails, setPenaltyDetails] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [rawStakeData, setRawStakeData] = useState(null);
  const [projectNames, setProjectNames] = useState({});
  
  // Updated useEffect to call the fetchStakingData function instead of defining it inline
  useEffect(() => {
    if (isReady && isConnected) {
      fetchStakingData();
    }
  }, [isReady, isConnected, walletAddress, services]);
  
  // Add the fetchStakingData function definition
  const fetchStakingData = async () => {
    if (!isReady || !isConnected) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get staking parameters
      const params = await services.projectStaking.getStakingParameters();
      setStakingParams(params);
      
      console.log('Using contract method to get all stakes...');
      
      // Use the contract method to get all active stakes across all projects
      try {
        const allStakes = await services.projectStaking.getAllUserStakesAcrossProjects(walletAddress);
        console.log('Retrieved all stakes across projects:', allStakes);
        
        if (allStakes && allStakes.length > 0) {
          setProjectStakes(allStakes);
          
          // Fetch project names for all projects
          const names = {};
          for (const project of allStakes) {
            names[project.projectId] = await getProjectName(project.projectId);
          }
          setProjectNames(names);
        } else {
          console.log('No active stakes found');
          setProjectStakes([]);
        }
      } catch (error) {
        console.error('Error retrieving stakes:', error);
        setError('Failed to load staking data. Please try refreshing.');
        setProjectStakes([]);
      }
    } catch (error) {
      console.error('Error fetching staking data:', error);
      setError('Failed to load staking portfolio');
      toast.error('Failed to load staking portfolio');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Helper function to get project name - this would normally come from your API or contract
  const getProjectName = async (projectId) => {
    try {
      // Try to get project name directly using the new getProjectName function
      if (services?.innovationUnits?.getProjectName) {
        try {
          const name = await services.innovationUnits.getProjectName(projectId);
          if (name) return name;
        } catch (e) {
          console.log(`Error getting project name for ${projectId} with getProjectName:`, e);
        }
      }
      
      // Fallback to getProjectData which includes name
      if (services?.innovationUnits?.getProjectData) {
        try {
          const projectData = await services.innovationUnits.getProjectData(projectId);
          if (projectData && projectData.name) return projectData.name;
        } catch (e) {
          console.log(`Error getting project data for ${projectId}:`, e);
        }
      }
      
      return `Project #${projectId}`;
    } catch (error) {
      console.error('Error getting project name:', error);
      return `Project #${projectId}`;
    }
  };
  
  // Handle unstaking
  const handleUnstake = async (projectId, stakeIndex) => {
    if (!isConnected || !isReady) {
      toast.error('Please connect your wallet');
      return;
    }
    
    try {
      setUnstaking({ projectId, stakeIndex });
      
      await services.projectStaking.unstake(projectId, stakeIndex);
      
      toast.success('Successfully unstaked CEL tokens');
      
      // Refresh data
      fetchStakingData();
    } catch (error) {
      console.error('Error unstaking:', error);
      toast.error('Failed to unstake tokens. ' + (error.message || ''));
    } finally {
      setUnstaking({ projectId: null, stakeIndex: null });
    }
  };

  // Show confirmation modal for early unstaking
  const showEarlyUnstakeConfirmation = async (projectId, stakeIndex) => {
    if (!isConnected || !isReady) {
      toast.error('Please connect your wallet');
      return;
    }
    
    try {
      // Calculate penalty details
      const details = await services.projectStaking.calculateEarlyUnstakePenalty(projectId, stakeIndex);
      console.log('Early unstake penalty details:', details);
      
      // Store penalty details for confirmation
      setPenaltyDetails({
        projectId,
        stakeIndex,
        ...details
      });
      
      // Show confirmation modal
      setShowConfirmModal(true);
    } catch (error) {
      console.error('Error calculating early unstake penalty:', error);
      toast.error('Failed to calculate early unstake penalty: ' + (error.message || ''));
    }
  };

  // Handle early unstaking
  const handleEarlyUnstake = async () => {
    if (!penaltyDetails || !isConnected || !isReady) {
      toast.error('Invalid operation');
      return;
    }
    
    const { projectId, stakeIndex } = penaltyDetails;
    
    try {
      setEarlyUnstaking({ projectId, stakeIndex });
      setShowConfirmModal(false);
      
      await services.projectStaking.earlyUnstake(projectId, stakeIndex);      
      // Refresh data
      fetchStakingData();
    } catch (error) {
      console.error('Error early unstaking:', error);
      toast.error('Failed to early unstake tokens: ' + (error.message || ''));
    } finally {
      setEarlyUnstaking({ projectId: null, stakeIndex: null });
      setPenaltyDetails(null);
    }
  };
  
  // Toggle debug mode
  const toggleDebugMode = () => {
    setDebugMode(!debugMode);
  };
  
  // Function to refresh staking data
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchStakingData();
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh staking data');
    } finally {
      setRefreshing(false);
    }
  };
  
  // Function to navigate to project 2 staking page - for the "No Stakes" message
  const handleStakeInProject2 = () => {
    // Navigate to project 2 staking page using react-router-dom
    navigate('/projects/token/2?tab=stake');
  };
  
  // Early Unstake Confirmation Modal
  const EarlyUnstakeModal = () => {
    if (!showConfirmModal || !penaltyDetails) return null;
    
    // Format penalty rate safely
    const formatPenaltyRate = () => {
      try {
        // If it's a number, format it
        if (typeof penaltyDetails.penaltyRate === 'number') {
          return penaltyDetails.penaltyRate.toFixed(2) + '%';
        }
        // If it's a BigNumber, convert and format it
        if (penaltyDetails.penaltyRate && typeof penaltyDetails.penaltyRate.toNumber === 'function') {
          return (penaltyDetails.penaltyRate.toNumber() / 100).toFixed(2) + '%';
        }
        // Fallback
        return (penaltyDetails.penaltyRate || 0) + '%';
      } catch (error) {
        console.error('Error formatting penalty rate:', error);
        // Safe fallback
        return `${(penaltyDetails.rawPenaltyRate || 0) / 100}%`;
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl border border-yellow-200">
          <div className="flex items-center mb-5">
            <div className="bg-yellow-100 p-2 rounded-lg mr-3">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Confirm Early Unstake</h3>
          </div>
          
          <div className="mb-5">
            <p className="text-gray-700 mb-4">
              You are unstaking before the lock period ends. This will incur a penalty.
            </p>
            
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-xl mb-4 border border-yellow-100 shadow-sm">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="text-gray-600 font-medium">Penalty amount:</div>
                <div className="font-semibold text-right text-red-600">{penaltyDetails.formattedPenaltyAmount} CEL</div>
                
                <div className="text-gray-600 font-medium">Penalty rate:</div>
                <div className="font-semibold text-right text-red-600">{formatPenaltyRate()}</div>
                
                <div className="text-gray-600 font-medium">You will receive:</div>
                <div className="font-semibold text-right text-indigo-600">{penaltyDetails.formattedReturnAmount} CEL</div>
                
                <div className="text-gray-600 font-medium">Time until unlock:</div>
                <div className="font-semibold text-right">{penaltyDetails.formattedRemainingTime}</div>
              </div>
            </div>
            
            <div className="bg-indigo-50 p-4 rounded-xl mb-4 text-sm text-indigo-700 border border-indigo-100">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 text-indigo-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="mb-1 font-medium">
                    Note: The penalty decreases as you get closer to the unlock time.
                  </p>
                  <p>
                    Penalty funds go to the project treasury to benefit all stakeholders.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3 justify-end">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleEarlyUnstake}
              className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-lg hover:from-yellow-600 hover:to-amber-700 transition-all font-medium shadow-sm"
            >
              Confirm Early Unstake
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Update the Return JSX
  if (!isConnected) {
    return (
      <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-sm text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
          <AlertCircle className="h-8 w-8 text-yellow-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-3">Wallet Not Connected</h3>
        <p className="text-gray-600 max-w-sm mx-auto">Connect your wallet to view and manage your staking portfolio</p>
      </div>
    );
  }
  
  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen ">
          <img 
            src={skyLoader} 
            alt="Loading..." 
            className="w-100 h-100 object-contain"
          />
        </div>
    );
  
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Early Unstake Modal */}
      <EarlyUnstakeModal />
      
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 text-white">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="flex items-center">
            <div className="bg-white/20 p-2 rounded-lg mr-4">
              <Wallet className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Staking Portfolio</h2>
              <p className="text-indigo-100 mt-1">
                Manage your active CEL token stakes across all projects
              </p>
            </div>
          </div>
          
          <button 
            onClick={handleRefresh} 
            disabled={refreshing || loading}
            className="px-4 py-2.5 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 flex items-center justify-center md:self-end transition-all"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {/* Main content */}
      <div className="p-6">
        {/* Display error if present */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <p className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              {error}
            </p>
            <p className="text-sm mt-2 ml-7">Try clicking "Check Project 2" button or refreshing the page.</p>
          </div>
        )}
        
        {/* Staking Parameters */}
        {stakingParams && (
          <div className="mb-8 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 bg-indigo-100/50 border-b border-indigo-100">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-indigo-600 mr-2" />
                <h3 className="text-lg font-semibold text-indigo-900">
                  Staking Parameters
                </h3>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Min Lock Period</p>
                  <div className="flex items-center">
                    <Hourglass className="h-4 w-4 text-indigo-500 mr-2" />
                    <span className="font-medium text-indigo-900">{stakingParams.minLockDays} days</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Max Lock Period</p>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-indigo-500 mr-2" />
                    <span className="font-medium text-indigo-900">{stakingParams.maxLockDays} days</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Multiplier Factor</p>
                  <div className="flex items-center">
                    <Coins className="h-4 w-4 text-indigo-500 mr-2" />
                    <span className="font-medium text-indigo-900">{stakingParams.multiplierFactor / stakingParams.scorePrecision}x</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Early Unstake Penalty</p>
                  <div className="flex items-center">
                    <AlertTriangle className="h-4 w-4 text-indigo-500 mr-2" />
                    <span className="font-medium text-indigo-900">
                      {(stakingParams.minPenaltyRate || 500) / 100}% - {(stakingParams.maxPenaltyRate || 5000) / 100}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Active Stakes */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <div className="bg-indigo-100 p-1.5 rounded-lg mr-3">
              <Wallet className="h-5 w-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Active Stakes</h3>
            {projectStakes.length > 0 && (
              <div className="ml-2 bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {projectStakes.reduce((total, project) => total + project.stakes.length, 0)} stakes across {projectStakes.length} project{projectStakes.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
          
          {projectStakes.length === 0 ? (
            <div className="text-center p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
                <Lock className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-700 mb-2">No Active Stakes</h4>
              <p className="text-gray-500 max-w-md mx-auto">
                You don't have any active stakes. Visit the project details page to stake CEL tokens.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {projectStakes.map((project) => (
                <div key={project.projectId} className="border border-indigo-100 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all">
                  <div 
                    onClick={() => navigate(`/projects/token/${project.projectId}`)}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4 border-b border-indigo-200 cursor-pointer hover:from-indigo-700 hover:to-purple-700 transition-all"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center">
                        <div className="bg-white/20 p-1.5 rounded-lg mr-3 flex-shrink-0">
                          <Coins className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-white text-lg leading-tight truncate max-w-xs group flex items-center">
                            {projectNames[project.projectId] || `Project #${project.projectId}`}
                            <ArrowRight className="h-4 w-4 ml-1.5 opacity-70" />
                          </h4>
                          <div className="text-indigo-100 text-sm flex items-center">
                            {project.stakes.length} active stake{project.stakes.length !== 1 ? 's' : ''} 
                            {project.stakes.filter(s => s.canUnstake).length > 0 && (
                              <span className="ml-2 bg-green-400/20 text-white text-xs px-2 py-0.5 rounded-full flex items-center">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {project.stakes.filter(s => s.canUnstake).length} ready to unstake
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div 
                        onClick={(e) => e.stopPropagation()} 
                        className="bg-indigo-300/30 text-white/90 text-xs px-2.5 py-1 rounded-full flex items-center self-start sm:self-auto"
                      >
                        <Shield className="h-3 w-3 mr-1" />
                        Project ID: {project.projectId}
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-2">
                    <div className="divide-y divide-indigo-100 space-y-6">
                      {project.stakes.map((stake, idx) => (
                        <div key={stake.stakeIndex} className="relative p-6 bg-white hover:bg-gray-50 transition-colors border-l-4 border-indigo-400 mx-2 rounded-lg shadow-md">
                          {/* Stake ribbon label */}
                          <div className="absolute -top-3 -left-1 bg-indigo-600 text-white px-3 py-1 rounded-r-lg shadow-sm flex items-center">
                            <div className="mr-1.5 bg-white text-indigo-600 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </div>
                            <span className="font-medium">Stake #{stake.stakeIndex}</span>
                          </div>

                          <div className="mb-3 pb-3 border-b border-indigo-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                            <div className="flex items-center">
                              <div className="ml-2">
                                {stake.canUnstake ? (
                                  <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Ready to unstake</span>
                                ) : (
                                  <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">Locked</span>
                                )}
                              </div>
                            </div>
                            <button 
                              onClick={() => navigate(`/projects/token/${project.projectId}?tab=stake`)}
                              className="text-xs bg-indigo-50 text-indigo-600 px-2 py-1 rounded-full flex items-center hover:bg-indigo-100 transition-colors self-start"
                            >
                              <Coins className="h-3 w-3 mr-1" />
                              Stake More in This Project
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-4 bg-indigo-50/30 p-4 rounded-lg">
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                              <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Amount</p>
                              <p className="font-semibold text-gray-900 flex items-center">
                                <Coins className="h-4 w-4 text-indigo-500 mr-2" />
                                {stake.formattedAmount} <span className="text-indigo-600 ml-1">CEL</span>
                              </p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                              <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Lock Duration</p>
                              <p className="font-semibold text-gray-900 flex items-center">
                                <Timer className="h-4 w-4 text-indigo-500 mr-2" />
                                {stake.formattedLockDuration}
                              </p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                              <p className="text-sm text-indigo-400 uppercase tracking-wider mb-2">Status</p>
                              {stake.canUnstake ? (
                                <p className="text-green-600 font-semibold flex items-center">
                                  <div className="h-3 w-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                                  <CheckCircle className="h-4 w-4 mr-1" /> Unlocked
                                </p>
                              ) : (
                                <p className="text-amber-600 font-semibold flex items-center">
                                  <div className="h-3 w-3 bg-amber-400 rounded-full mr-2 animate-pulse"></div>
                                  <Clock className="h-4 w-4 mr-1" /> Locked for {stake.timeUntilUnlock}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
                            <div className="flex items-center bg-gray-50 p-3 rounded-lg">
                              <Calendar className="h-4 w-4 text-indigo-500 mr-2" />
                              <div>
                                <span className="text-gray-500 block text-xs">Start Date:</span>
                                <span className="font-medium text-gray-700">
                                  {new Date(stake.startTime * 1000).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center bg-gray-50 p-3 rounded-lg">
                              <Calendar className="h-4 w-4 text-indigo-500 mr-2" />
                              <div>
                                <span className="text-gray-500 block text-xs">Unlock Date:</span>
                                <span className="font-medium text-gray-700">
                                  {new Date(stake.unlockTime * 1000).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center bg-indigo-50 p-3 rounded-lg">
                              <Shield className="h-4 w-4 text-indigo-500 mr-2" />
                              <div>
                                <span className="text-indigo-500 block text-xs">Staking Score:</span>
                                <span className="font-medium text-indigo-700">
                                  {parseFloat(ethers.utils.formatEther(stake.score)).toFixed(2)} points
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex justify-end pt-2 border-t border-gray-100 space-x-3">
                            {/* Always show both buttons, but enable/disable based on state */}
                            <button 
                              onClick={() => handleUnstake(project.projectId, stake.stakeIndex)}
                              disabled={!stake.canUnstake || unstaking.projectId === project.projectId && unstaking.stakeIndex === stake.stakeIndex}
                              className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all flex items-center"
                              title={!stake.canUnstake ? "Stake is still locked" : "Unstake your tokens"}
                            >
                              {unstaking.projectId === project.projectId && unstaking.stakeIndex === stake.stakeIndex ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                                  Unstaking...
                                </>
                              ) : (
                                <>
                                  Unstake <ArrowRight className="ml-2 h-4 w-4" />
                                </>
                              )}
                            </button>
                            
                            <button
                              onClick={() => showEarlyUnstakeConfirmation(project.projectId, stake.stakeIndex)}
                              disabled={
                                stake.canUnstake || 
                                (earlyUnstaking.projectId === project.projectId && earlyUnstaking.stakeIndex === stake.stakeIndex) ||
                                showConfirmModal
                              }
                              className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-lg hover:from-yellow-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all flex items-center"
                              title={stake.canUnstake ? "Stake is already unlocked" : "Unstake early with penalty"}
                            >
                              {earlyUnstaking.projectId === project.projectId && earlyUnstaking.stakeIndex === stake.stakeIndex ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                                  Unstaking Early...
                                </>
                              ) : (
                                <>
                                  Early Unstake <AlertTriangle className="ml-2 h-4 w-4" />
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Info Section */}
        <div className="mt-10 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 rounded-xl p-6 border border-indigo-100 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-indigo-100 p-1.5 rounded-lg mr-3">
              <Shield className="h-5 w-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-indigo-900">About Staking</h3>
          </div>
          
          <div className="bg-white rounded-lg p-5 shadow-sm">
            <p className="text-gray-700 mb-3">
              Staking CEL tokens on projects helps you:
            </p>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start">
                <div className="bg-green-100 p-1 rounded-md mr-3 mt-0.5">
                  <Coins className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-gray-700">
                  <span className="font-medium text-green-700">Earn emissions</span> from the protocol based on your stake size and duration
                </p>
              </div>
              <div className="flex items-start">
                <div className="bg-purple-100 p-1 rounded-md mr-3 mt-0.5">
                  <CheckCircle className="h-4 w-4 text-purple-600" />
                </div>
                <p className="text-gray-700">
                  <span className="font-medium text-purple-700">Support projects</span> you believe in
                </p>
              </div>
              <div className="flex items-start">
                <div className="bg-blue-100 p-1 rounded-md mr-3 mt-0.5">
                  <Shield className="h-4 w-4 text-blue-600" />
                </div>
                <p className="text-gray-700">
                  <span className="font-medium text-blue-700">Increase your influence</span> in protocol governance
                </p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg mb-3">
              The longer you lock your tokens, the higher your staking score and potential rewards.
            </p>
            
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm">
              <div className="flex items-start">
                <AlertTriangle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-amber-700">
                  Early unstaking is available but incurs a penalty that decreases as you get closer to the unlock time.
                  Penalty funds go to the project treasury.
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Debug toggle */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={toggleDebugMode}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            {debugMode ? 'Hide Debug Info' : 'Show Debug Info'}
          </button>
        </div>
        
        {/* Debug panel */}
        {debugMode && (
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono">
            <h3 className="font-semibold mb-2">Debug Information:</h3>
            <div>Wallet Address: {walletAddress}</div>
            <div>Is Connected: {isConnected ? 'Yes' : 'No'}</div>
            <div>Is Ready: {isReady ? 'Yes' : 'No'}</div>
            <div>Projects with Stakes: {projectStakes.length}</div>
            
            <h4 className="font-semibold mt-3 mb-1">Raw Stake Data for Project 2:</h4>
            <pre className="bg-gray-100 p-2 rounded overflow-auto max-h-40">
              {rawStakeData ? JSON.stringify(rawStakeData, null, 2) : 'No data available'}
            </pre>
          </div>
        )}
        
        {/* No stakes message */}
        {!loading && projectStakes.length === 0 && (
          <div className="py-10 mt-8 text-center bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-100 shadow-sm">
            <div className="inline-flex bg-yellow-100 rounded-full p-3 mb-5">
              <Lock className="h-8 w-8 text-yellow-600" />
            </div>
            <h3 className="text-xl font-semibold text-indigo-900 mb-3">No Active Stakes Found</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-4">
              You don't have any active stakes yet. Start earning rewards by staking your CEL tokens in a project.
            </p>
            <div className="bg-white p-4 rounded-lg mx-auto max-w-xs mb-8 border border-indigo-100 shadow-sm">
              <div className="flex items-center text-left mb-2">
                <div className="bg-indigo-100 p-1.5 rounded-lg mr-3">
                  <Coins className="h-5 w-5 text-indigo-600" />
                </div>
                <span 
                  onClick={() => navigate('/projects/token/2')} 
                  className="text-lg font-medium text-gray-800 cursor-pointer hover:text-indigo-600 hover:underline flex items-center"
                >
                  Project 2
                  <ArrowRight className="h-4 w-4 ml-1 text-indigo-500" />
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-3">Recommended for new stakers</p>
              <button
                onClick={handleStakeInProject2}
                className="w-full inline-flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-700 shadow-sm transition-all"
              >
                Stake in Project 2
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StakingPortfolioTab; 